import React, { useEffect, useMemo, useState } from 'react';
import { useModuleConfig } from '@/hooks/queries/admin/useModuleConfig';
import type { IemModuleConfig } from '@/types/moduleConfig';
import { useIeKitLabels } from '@/hooks/useIeKitLabels';
import { Box, Card, CardContent, Tab, Tabs } from '@mui/material';
import { AllInboxOutlined, HistoryOutlined, Inventory2Outlined } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { IeTable } from '@/components/iem/tables/IeTable';
import { AllocationTab } from '@/components/iem/tabs/AllocationTab';
import { TransactionHistoryTab } from '@/components/iem/tabs/TransactionHistoryTab';
import { IemContextualActions } from '@/components/iem/actions/IemContextualActions';
import { TabActionsContext } from '@/components/iem/panels/TabActionsContext';

export type TabKey = 'allocation' | 'ie' | 'transaction-history';

const tabKeys: TabKey[] = ['allocation', 'ie', 'transaction-history'];

// a11y helpers for Tab/TabPanel linkage
const a11yProps = (name: TabKey, index: number) => ({
  id: `iem-tab-${index}-${name}`,
  'aria-controls': `iem-tabpanel-${index}-${name}`,
});

type TabPanelProps = {
  children?: React.ReactNode;
  value: number;
  index: number;
  name: TabKey;
};

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, name }) => {
  const isActive = value === index;
  return (
    <div
      role="tabpanel"
      hidden={!isActive}
      id={`iem-tabpanel-${index}-${name}`}
      aria-labelledby={`iem-tab-${index}-${name}`}
    >
      {isActive && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

const routeForTab = (key: TabKey) =>
  key === 'allocation' ? '/customer-service' : `/customer-service/${key}`;

export const MainContentTabs: React.FC = () => {
  const { config: iemConfig } = useModuleConfig<IemModuleConfig>('iem');
  const iemLabels = iemConfig.features.front_window;
  const { tabLabel: ieKitTabLabel } = useIeKitLabels('front_window');
  const navigate = useNavigate();
  const params = useParams();
  const activeTabParam = params.activeTab as TabKey | undefined;
  const initialIndex =
    activeTabParam && tabKeys.includes(activeTabParam) ? tabKeys.indexOf(activeTabParam) : 0;
  const [tabIndex, setTabIndex] = useState(initialIndex);
  const [tabActions, setTabActions] = useState<React.ReactNode>(null);

  // Keep internal state in sync when URL param changes (e.g., back/forward navigation)
  useEffect(() => {
    if (activeTabParam && tabKeys.includes(activeTabParam)) {
      const newIdx = tabKeys.indexOf(activeTabParam);
      if (newIdx !== tabIndex) setTabIndex(newIdx);
    } else if (!activeTabParam && tabIndex !== 0) {
      setTabIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabParam]);

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
    const key = tabKeys[newValue] ?? 'allocation';
    navigate(routeForTab(key), { replace: false });
  };

  // Ensure left/right arrow keys move selection when Tabs have focus
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      setTabIndex((prev) => {
        const delta = e.key === 'ArrowRight' ? 1 : -1;
        const next = (prev + tabKeys.length + delta) % tabKeys.length;
        const key = tabKeys[next] ?? 'allocation';
        navigate(routeForTab(key));
        return next;
      });
    }
  };

  // Memoize context value to prevent infinite re-render loops in consumers
  const tabActionsContextValue = useMemo(() => ({ setActions: setTabActions }), []);

  return (
    <Card>
      <CardContent sx={{ pt: 2 }}>
        <Box sx={{ mb: 2 }}>
          <IemContextualActions />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Tabs
            value={tabIndex}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            aria-label={iemLabels.main_content_tabs_label}
            variant="scrollable"
            allowScrollButtonsMobile
          >
            <Tab
              icon={<AllInboxOutlined fontSize="small" />}
              iconPosition="top"
              label="Allocation"
              {...a11yProps('allocation', 0)}
            />
            <Tab
              icon={<Inventory2Outlined fontSize="small" />}
              iconPosition="top"
              label={ieKitTabLabel}
              {...a11yProps('ie', 1)}
            />
            <Tab
              icon={<HistoryOutlined fontSize="small" />}
              iconPosition="top"
              label="Transaction History"
              {...a11yProps('transaction-history', 2)}
            />
          </Tabs>
          {tabActions && <Box sx={{ display: 'flex', gap: 1 }}>{tabActions}</Box>}
        </Box>

        <TabActionsContext.Provider value={tabActionsContextValue}>
          <TabPanel value={tabIndex} index={0} name="allocation">
            <AllocationTab />
          </TabPanel>

          <TabPanel value={tabIndex} index={1} name="ie">
            <IeTable onClearError={() => {}} />
          </TabPanel>

          <TabPanel value={tabIndex} index={2} name="transaction-history">
            <TransactionHistoryTab />
          </TabPanel>
        </TabActionsContext.Provider>
      </CardContent>
    </Card>
  );
};
